import { useQuery } from '@tanstack/react-query';
import { Suspense, type CSSProperties, type ReactNode } from 'react';
import { authApi, type StudentShellSettings } from '@/features/auth/api/authApi';
import { StudentLocaleProvider } from '../providers/StudentLocaleProvider';
import { useThemeStore } from '../providers/ThemeProvider';

export const DEFAULT_STUDENT_THEME_TOKENS: Record<string, string> = {
  surfaceBackground: '#FFFFFF',
  surfaceDefault: '#F8F9FA',
  surfaceRaised: '#FFFFFF',
  surfaceMuted: '#F5F5F5',
  textPrimary: '#171717',
  textSecondary: '#525252',
  textTertiary: '#737373',
  borderDefault: '#E5E5E5',
  borderStrong: '#D4D4D4',
  primary: '#2563EB',
  primaryHover: '#1D4ED8',
  primaryLight: '#EFF6FF',
  primaryForeground: '#FFFFFF',
  focusRing: '#3B82F6',
  success: '#22C55E',
  successLight: '#F0FDF4',
  warning: '#F59E0B',
  warningLight: '#FFFBEB',
  danger: '#EF4444',
  dangerLight: '#FEF2F2',
  info: '#3B82F6',
  infoLight: '#EFF6FF',
  practiceAction: '#2563EB',
  practiceActionHover: '#1D4ED8',
  practiceActionLight: '#EFF6FF',
  practiceActionForeground: '#FFFFFF',
  sidebarBackground: '#F8F9FA',
  sidebarForeground: '#171717',
  sidebarTypeSelectedColor: '#3B82F6',
  practicePromptAccent: '#3B82F6',
  practicePromptBackground: '#EFF6FF',
  practicePromptForeground: '#171717',
  recordingIdle: '#3B82F6',
  recordingPreparing: '#3B82F6',
  recordingActive: '#EF4444',
  recordingComplete: '#22C55E',
  recordingSaving: '#2563EB',
  sectionSpeaking: '#3B82F6',
  sectionWriting: '#3B82F6',
  sectionReading: '#3B82F6',
  sectionListening: '#3B82F6',
  contentFeatured: '#92400E',
  contentFeaturedLight: '#FFF7E6',
  contentNew: '#2563EB',
  contentNewLight: '#EFF6FF',
};

export const PROTOTYPE_THEME_RUNTIME_ALIASES = {
  sidebarHoverBackground: '#2E3348',
  sidebarActiveBackground: '#3A4060',
  sidebarActiveForeground: '#FFFFFF',
  sidebarActiveBorder: '#8A9EB5',
  audioAction: '#3A5F9A',
  audioActionHover: '#2D4A8A',
  audioActionLight: '#F0F4F8',
  answerAction: '#2D4A8A',
  answerActionHover: '#243B6E',
  answerActionLight: '#F0F4F8',
  speakingLight: '#EAEFF5',
  writingLight: '#F0EAEF',
  readingLight: '#EEF0EB',
  listeningLight: '#F0EEEB',
};

export type StudentShellThemeStyle = CSSProperties & Record<`--${string}`, string | number>;

function StudentThemeLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]" role="status" aria-label="Loading">
      <span className="h-8 w-8 animate-spin rounded-[var(--radius-full)] border-2 border-[var(--color-border)] border-t-[var(--color-primary)]" aria-hidden="true" />
    </div>
  );
}

function resolveThemeTokens(shellSettings: StudentShellSettings | null | undefined) {
  return Object.fromEntries(
    Object.entries(DEFAULT_STUDENT_THEME_TOKENS).map(([key, fallback]) => {
      const candidate = shellSettings?.themeTokens?.[key];
      return [key, candidate && /^#[0-9A-F]{6}$/i.test(candidate) ? candidate : fallback];
    }),
  ) as Record<string, string>;
}

export function resolveStudentThemeStyle(
  shellSettings: StudentShellSettings | null | undefined,
  theme: 'light' | 'dark',
  isPracticeRoute: boolean,
  layoutStyle?: StudentShellThemeStyle,
): StudentShellThemeStyle {
  const themeTokens = resolveThemeTokens(shellSettings);
  const usePrototypeThemeFidelity = theme === 'light' && shellSettings?.themePreset === 'archive_home';
  const usePracticeAccent = isPracticeRoute
    && shellSettings?.practiceColorMode === 'use_practice_accent';
  const practiceAction = usePracticeAccent
    ? themeTokens.practiceAction
    : theme === 'dark' ? 'var(--color-primary)' : themeTokens.primary;
  const practiceActionHover = usePracticeAccent
    ? themeTokens.practiceActionHover
    : theme === 'dark' ? 'var(--color-primary-hover)' : themeTokens.primaryHover;
  const practiceActionLight = usePracticeAccent
    ? themeTokens.practiceActionLight
    : theme === 'dark' ? 'var(--color-primary-light)' : themeTokens.primaryLight;
  const practiceActionForeground = usePracticeAccent
    ? themeTokens.practiceActionForeground
    : theme === 'dark' ? 'var(--color-primary-foreground)' : themeTokens.primaryForeground;
  const scopedPrimaryLight = theme === 'dark'
    ? `color-mix(in srgb, ${practiceAction} 24%, var(--color-surface-raised))`
    : practiceActionLight;
  const scopedActionIndicator = theme === 'dark'
    ? `color-mix(in srgb, ${practiceAction} 52%, #FFFFFF)`
    : practiceAction;
  const scopedActionIndicatorHover = theme === 'dark'
    ? `color-mix(in srgb, ${practiceActionHover} 40%, #FFFFFF)`
    : practiceActionHover;
  const contentFeatured = theme === 'dark'
    ? `color-mix(in srgb, ${themeTokens.contentFeatured} 62%, #FFFFFF)`
    : themeTokens.contentFeatured;
  const contentFeaturedLight = theme === 'dark'
    ? `color-mix(in srgb, ${themeTokens.contentFeatured} 18%, var(--color-surface-raised))`
    : themeTokens.contentFeaturedLight;
  const contentNew = theme === 'dark'
    ? `color-mix(in srgb, ${themeTokens.contentNew} 62%, #FFFFFF)`
    : themeTokens.contentNew;
  const contentNewLight = theme === 'dark'
    ? `color-mix(in srgb, ${themeTokens.contentNew} 18%, var(--color-surface-raised))`
    : themeTokens.contentNewLight;
  const practiceFocusRing = usePracticeAccent
    ? (theme === 'dark' ? `color-mix(in srgb, ${themeTokens.focusRing} 52%, #FFFFFF)` : themeTokens.focusRing)
    : 'var(--color-focus-ring)';
  const recordingSaving = shellSettings?.themePreset === 'custom'
    ? themeTokens.recordingSaving
    : practiceAction;

  return {
    ...layoutStyle,
    ...(theme === 'light' ? {
      '--color-bg': themeTokens.surfaceBackground,
      '--color-surface': themeTokens.surfaceDefault,
      '--color-surface-raised': themeTokens.surfaceRaised,
      '--color-surface-muted': themeTokens.surfaceMuted,
      '--color-text': themeTokens.textPrimary,
      '--color-text-secondary': themeTokens.textSecondary,
      '--color-text-tertiary': themeTokens.textTertiary,
      '--color-border': themeTokens.borderDefault,
      '--color-border-strong': themeTokens.borderStrong,
      '--color-primary': usePracticeAccent ? themeTokens.practiceAction : themeTokens.primary,
      '--color-primary-hover': usePracticeAccent ? themeTokens.practiceActionHover : themeTokens.primaryHover,
      '--color-primary-light': usePracticeAccent ? themeTokens.practiceActionLight : themeTokens.primaryLight,
      '--color-primary-foreground': usePracticeAccent ? themeTokens.practiceActionForeground : themeTokens.primaryForeground,
      '--color-text-inverse': usePracticeAccent ? themeTokens.practiceActionForeground : themeTokens.primaryForeground,
      '--color-focus-ring': themeTokens.focusRing,
      '--color-success': themeTokens.success,
      '--color-success-light': themeTokens.successLight,
      '--color-warning': themeTokens.warning,
      '--color-warning-light': themeTokens.warningLight,
      '--color-danger': themeTokens.danger,
      '--color-danger-light': themeTokens.dangerLight,
      '--color-info': themeTokens.info,
      '--color-info-light': themeTokens.infoLight,
    } : {}),
    '--color-content-featured': contentFeatured,
    '--color-content-featured-light': contentFeaturedLight,
    '--color-content-new': contentNew,
    '--color-content-new-light': contentNewLight,
    '--color-shell-sidebar-bg': themeTokens.sidebarBackground,
    '--color-shell-sidebar-foreground': themeTokens.sidebarForeground,
    '--color-shell-sidebar-hover-bg': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarHoverBackground
      : `color-mix(in srgb, ${themeTokens.sidebarForeground} 8%, ${themeTokens.sidebarBackground})`,
    '--color-shell-sidebar-active-bg': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveBackground
      : `color-mix(in srgb, ${themeTokens.sidebarForeground} 14%, ${themeTokens.sidebarBackground})`,
    '--color-shell-sidebar-active-fg': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveForeground
      : themeTokens.sidebarForeground,
    '--color-shell-sidebar-active-border': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveBorder
      : themeTokens.focusRing,
    '--color-shell-sidebar-type-selected-bg': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveBackground
      : `color-mix(in srgb, ${themeTokens.sidebarTypeSelectedColor} 22%, ${themeTokens.sidebarBackground})`,
    '--color-shell-sidebar-type-selected-fg': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveForeground
      : themeTokens.sidebarForeground,
    '--color-shell-sidebar-type-selected-border': usePrototypeThemeFidelity
      ? PROTOTYPE_THEME_RUNTIME_ALIASES.sidebarActiveBorder
      : themeTokens.sidebarTypeSelectedColor,
    '--color-practice-drawer-hover-bg': themeTokens.surfaceMuted,
    '--color-practice-drawer-active-bg': scopedPrimaryLight,
    '--color-practice-drawer-active-fg': scopedActionIndicator,
    '--color-practice-drawer-active-border': scopedActionIndicator,
    '--color-practice-drawer-current-ring': practiceFocusRing,
    '--color-practice-timer-warning': scopedActionIndicator,
    '--color-practice-timer-warning-light': scopedPrimaryLight,
    '--color-practice-notice': scopedActionIndicator,
    '--color-practice-notice-light': scopedPrimaryLight,
    '--color-practice-status-done': themeTokens.success,
    '--color-practice-status-done-light': themeTokens.successLight,
    '--color-practice-status-tried': scopedActionIndicator,
    '--color-practice-status-tried-light': scopedPrimaryLight,
    '--color-practice-status-new': theme === 'dark' ? 'var(--color-text-secondary)' : themeTokens.textSecondary,
    '--color-practice-status-new-light': theme === 'dark' ? 'var(--color-surface)' : themeTokens.surfaceDefault,
    '--color-practice-prompt-accent': themeTokens.practicePromptAccent,
    '--color-practice-prompt-background': shellSettings?.showPracticePromptBackground === false
      ? 'transparent'
      : theme === 'dark'
        ? `color-mix(in srgb, ${themeTokens.practicePromptAccent} 18%, var(--color-surface-raised))`
        : themeTokens.practicePromptBackground,
    '--color-practice-prompt-foreground': theme === 'dark' ? 'var(--color-text)' : themeTokens.practicePromptForeground,
    '--color-recording-idle': themeTokens.recordingIdle,
    '--color-recording-preparing': themeTokens.recordingPreparing,
    '--color-recording-active': themeTokens.recordingActive,
    '--color-recording-complete': themeTokens.recordingComplete,
    '--color-recording-saving': recordingSaving,
    '--color-practice-action': practiceAction,
    '--color-practice-action-hover': practiceActionHover,
    '--color-practice-action-light': scopedPrimaryLight,
    '--color-practice-action-foreground': practiceActionForeground,
    '--color-practice-focus-ring': practiceFocusRing,
    '--color-audio-action': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.audioAction : practiceAction,
    '--color-audio-action-hover': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.audioActionHover : practiceActionHover,
    '--color-audio-action-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.audioActionLight : scopedPrimaryLight,
    '--color-answer-action': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.answerAction : scopedActionIndicator,
    '--color-answer-action-hover': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.answerActionHover : scopedActionIndicatorHover,
    '--color-answer-action-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.answerActionLight : scopedPrimaryLight,
    '--color-speaking': themeTokens.sectionSpeaking,
    '--color-writing': themeTokens.sectionWriting,
    '--color-reading': themeTokens.sectionReading,
    '--color-listening': themeTokens.sectionListening,
    '--color-speaking-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.speakingLight : `color-mix(in srgb, ${themeTokens.sectionSpeaking} 14%, var(--color-surface-raised))`,
    '--color-writing-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.writingLight : `color-mix(in srgb, ${themeTokens.sectionWriting} 14%, var(--color-surface-raised))`,
    '--color-reading-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.readingLight : `color-mix(in srgb, ${themeTokens.sectionReading} 14%, var(--color-surface-raised))`,
    '--color-listening-light': usePrototypeThemeFidelity ? PROTOTYPE_THEME_RUNTIME_ALIASES.listeningLight : `color-mix(in srgb, ${themeTokens.sectionListening} 14%, var(--color-surface-raised))`,
  };
}

export function StudentThemeBoundary({ children }: { children: ReactNode }) {
  const { theme } = useThemeStore();
  const { data: shellSettings = null } = useQuery({
    queryKey: ['student-shell-settings'],
    queryFn: ({ signal }) => authApi.getShellSettings({ signal }),
    staleTime: 60_000,
  });
  const locale = shellSettings?.studentUiLanguage || 'zh-CN';
  const style = resolveStudentThemeStyle(shellSettings, theme, true);

  return (
    <StudentLocaleProvider locale={locale}>
      <div className="student-shell-theme min-h-screen min-w-0 bg-[var(--color-bg)]" style={style}>
        <Suspense fallback={<StudentThemeLoadingFallback />}>
          {children}
        </Suspense>
      </div>
    </StudentLocaleProvider>
  );
}
